import { DeactivationPermission } from '@ceres/shared/services';
import { NgModule } from '@angular/core';
import { RouterModule, Routes } from '@angular/router';
import { FeatureToggleGuard, property } from '@ceres/feature-toggles';
import { rolePermissions as Permissions } from '@ceres/domain';
import { CeresFeaturesConfig } from '@ceres/shared/features';
import { HomeComponent } from './home/home.component';
import { RolePermissionGuard } from './shared/services/guards/role-permission.guard';
import { SchedulingGuard } from './shared/services/guards/scheduling.guard';
import { MsalGuard } from '@azure/msal-angular';

const routes: Routes = [
    {
        path: 'services',
        loadChildren: () => import('./services/features-services.module').then((m) => m.FeaturesServicesModule),
        data: {
            features: [property<CeresFeaturesConfig>().services.enabled],
            permission: Permissions.services.readAll
        },
        canActivate: [MsalGuard, RolePermissionGuard, FeatureToggleGuard]
    },
    {
        path: 'projects',
        loadChildren: () => import('./project/project.module').then((m) => m.ProjectModule),
        data: {
            features: [property<CeresFeaturesConfig>().projects.enabled]
        },
        canActivate: [MsalGuard, FeatureToggleGuard]
    },
    {
        path: 'timesheet',
        loadChildren: () => import('./working-hours/working-hours.module').then((m) => m.WorkingHoursModule),
        data: {
            features: [property<CeresFeaturesConfig>().timeManagement.timeSheets]
        },
        canActivate: [MsalGuard, FeatureToggleGuard]
    },
    {
        path: 'tasks',
        loadChildren: () => import('./tasks/tasks.module').then((m) => m.TasksModule),
        canActivate: [MsalGuard]
    },
    {
        path: 'test',
        loadChildren: () => import('./test/test.module').then((m) => m.TestModule),
        canActivate: [MsalGuard]
    },
    {
        path: 'projectgroup',
        loadChildren: () => import('./program/program.module').then((m) => m.ProgramModule),
        data: {
            features: [property<CeresFeaturesConfig>().projects.groups.enabled]
        },
        canActivate: [MsalGuard, FeatureToggleGuard]
    },
    {
        path: 'myceres',
        loadChildren: () => import('./myceres/myceres.module').then((m) => m.MyceresModule),
        canActivate: [MsalGuard]
    },
    {
        path: 'employees',
        loadChildren: () => import('./employees/employees.module').then((m) => m.EmployeeModule),
        canActivate: [MsalGuard]
    },
    {
        path: 'contract',
        loadChildren: () => import('./contract/contract.module').then((m) => m.ContractModule),
        data: {
            features: [property<CeresFeaturesConfig>().sales.offers]
        },
        canActivate: [MsalGuard, FeatureToggleGuard]
    },
    {
        path: 'capacities',
        loadChildren: () => import('./capacity/capacity.module').then((m) => m.CapacityModule),
        data: {
            features: [property<CeresFeaturesConfig>().timeManagement.capacity]
        },
        canActivate: [MsalGuard, FeatureToggleGuard]
    },
    {
        path: 'absences',
        loadChildren: () => import('./absences/absences.module').then((m) => m.AbsencesModule),
        data: {
            features: [property<CeresFeaturesConfig>().timeManagement.absences]
        },
        canActivate: [MsalGuard, FeatureToggleGuard]
    },
    {
        path: 'workload',
        loadChildren: () => import('./workload/workload.module').then((m) => m.WorkloadModule),
        data: {
            features: [property<CeresFeaturesConfig>().timeManagement.workload]
        },
        canActivate: [MsalGuard, FeatureToggleGuard]
    },
    {
        path: 'transactional-hours',
        loadChildren: () =>
            import('./transactional-hours/transactional-hours.module').then((m) => m.TransactionalHoursModule),
        data: {
            features: [property<CeresFeaturesConfig>().timeManagement.transactionalTimeTracking]
        },
        canActivate: [MsalGuard, FeatureToggleGuard]
    },
    {
        path: 'businesspartner',
        loadChildren: () => import('./business-partner/business-partner.module').then((m) => m.BusinessPartnerModule),
        data: {
            features: [property<CeresFeaturesConfig>().businessPartner.enabled]
        },
        canActivate: [MsalGuard, FeatureToggleGuard]
    },
    {
        path: 'bop',
        loadChildren: () => import('./bop/bop.module').then((m) => m.BopModule),
        data: {
            features: [property<CeresFeaturesConfig>().sales.businessOpportunities]
        },
        canActivate: [MsalGuard, FeatureToggleGuard]
    },
    {
        path: 'admin',
        loadChildren: () => import('./admin/admin.module').then((m) => m.AdminModule),
        data: {
            features: [property<CeresFeaturesConfig>().administration.enabled]
        },
        canActivate: [MsalGuard, FeatureToggleGuard]
    },
    {
        path: 'reports',
        loadChildren: () => import('./reports/reports.module').then((m) => m.ReportsModule),
        data: {
            features: [property<CeresFeaturesConfig>().reporting.enabled]
        },
        canActivate: [MsalGuard, FeatureToggleGuard]
    },
    {
        path: 'gmsreporting',
        loadChildren: () => import('./gmsreporting/gmsreporting.module').then((m) => m.GmsreportingModule),
        data: {
            features: [property<CeresFeaturesConfig>().reporting.gmsReportingDemo]
        },
        canActivate: [MsalGuard, FeatureToggleGuard]
    },
    {
        path: 'sales-activity',
        loadChildren: () => import('./sales-activity/sales-activity.module').then((m) => m.SalesActivityModule),
        data: {
            features: [property<CeresFeaturesConfig>().sales.salesActivities]
        },
        canActivate: [MsalGuard, FeatureToggleGuard]
    },
    {
        path: 'controlling',
        loadChildren: () => import('./controlling/controlling.module').then((m) => m.ControllingModule),
        data: {
            features: [property<CeresFeaturesConfig>().reporting.enabled]
        },
        canActivate: [MsalGuard, FeatureToggleGuard]
    },
    {
        path: 'sales-funnel',
        loadChildren: () => import('./controlling/controlling.module').then((m) => m.ControllingModule),
        data: {
            features: [property<CeresFeaturesConfig>().sales.salesFunnel]
        },
        canActivate: [MsalGuard, FeatureToggleGuard]
    },
    {
        path: 'quality',
        loadChildren: () => import('./controlling/controlling.module').then((m) => m.ControllingModule),
        data: {
            features: [property<CeresFeaturesConfig>().reporting.enabled]
        },
        canActivate: [MsalGuard, FeatureToggleGuard]
    },
    {
        path: 'cost-report',
        loadChildren: () => import('./cost-report/cost-report.module').then((m) => m.CostReportModule),
        data: {
            features: [property<CeresFeaturesConfig>().reporting.costReport]
        },
        canActivate: [MsalGuard, FeatureToggleGuard]
    },
    {
        path: 'working-hours-report',
        loadChildren: () =>
            import('./working-hours-report/working-hours-report.module').then((m) => m.WorkingHoursReportModule),
        data: {
            features: [property<CeresFeaturesConfig>().reporting.workingHoursReport]
        },
        canActivate: [MsalGuard, FeatureToggleGuard]
    },
    {
        path: 'accounting',
        loadChildren: () => import('./conaktiv/conaktiv.module').then((m) => m.ConAktivModule),
        data: { features: [property<CeresFeaturesConfig>().projects.charging] },
        canActivate: [MsalGuard, FeatureToggleGuard]
    },
    /*  {
    path: 'prestaffing',
    loadChildren: () =>
      import('./pre-staffing/pre-staffing.module').then(
        (m) => m.PreStaffingModule,
      ),
    data: {
      features: [property<CeresFeaturesConfig>().projects.preStaffing],
      permission: Permissions.project.preStaffing.readAll
    },
    canActivate: [ MsalGuard, FeatureToggleGuard, RolePermissionGuard],
  },*/
    {
        path: 'prestaffing',
        loadChildren: () => import('./pre-staffing-v2/pre-staffing-v2.module').then((m) => m.PreStaffingV2Module),
        canActivate: [MsalGuard]
    },
    {
        path: 'scheduling',
        loadChildren: () => import('./scheduling/scheduling.module').then((m) => m.SchedulingModule),
        data: {
            features: [property<CeresFeaturesConfig>().scheduling.scheduler]
        },
        canActivate: [MsalGuard, FeatureToggleGuard, SchedulingGuard]
    },
    {
        path: 'analytics',
        loadChildren: () => import('./analytics/analytics.module').then((m) => m.AnalyticsModule),
        data: {
            features: [property<CeresFeaturesConfig>().scheduling.analytics]
        },
        canActivate: [MsalGuard, FeatureToggleGuard]
    },
    {
        path: 'unauthorized',
        loadChildren: () => import('./unauthorized/unauthorized.module').then((m) => m.UnauthorizedModule)
    },
    {
        path: '',
        pathMatch: 'full',
        component: HomeComponent,
        canActivate: [MsalGuard]
    },
    {
        path: 'error',
        component: HomeComponent,
        canDeactivate: [DeactivationPermission]
    }
    // { path: '**', redirectTo: '/error' }
];

@NgModule({
    imports: [
        RouterModule.forRoot(routes, {
            useHash: false,
            enableTracing: false
        })
    ],
    exports: [RouterModule]
})
export class AppRoutingModule {}
